import useMobile from "@/app/hooks/use-mobile";
import { ISINPrices } from "@/app/interface/assetpage";
import { UEListData } from "@/app/interface/homepage";
import { formatIndianCurrency } from "@/app/utils/helper";
import FinancialMetrics from "@/app/alts/unlisted-shares/components/fundamental-sheet";
import { ChevronDown, ChevronUp } from "lucide-react";
import Image from "next/image";
import React, { useState } from "react";
import LoadingIcon from "@/app/assets/icons/loading";

interface BasketCardProps {
    stock: UEListData;
    basketPrices: ISINPrices[];
}

export const BasketCard: React.FC<BasketCardProps> = ({ stock, basketPrices }) => {
    const [isExpanded, setisExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState<string>("Fundamentals");

    const { isMobile } = useMobile();
    const ISINUnitPrice = basketPrices?.find((price) => price.ISIN === stock.ISIN)?.unitPrice || 0;

    return !isMobile ? (
        <div className="mb-6 rounded-xl border border-dashed p-s9">
            <div className="flex-start flex items-center justify-between gap-s10">
                <Image
                    src={stock?.company?.logoUrl}
                    alt={stock?.company?.displayName}
                    width={87}
                    height={87}
                />
                <h3
                    className="line-clamp-2 max-w-[179px] text-lg font-semibold leading-[130%]"
                    title={stock?.company?.displayName}
                >
                    {stock?.company?.displayName}
                </h3>
                <div className="flex items-center justify-between gap-s11 text-center">
                    <div className="flex flex-col gap-3">
                        <p className="text-sm text-neutral-300">MARKET CAP</p>
                        <p>{stock?.distribution?.marketCap ?? "--"}</p>
                    </div>

                    <div className="flex flex-col gap-3">
                        <p className="text-sm text-neutral-300">Current Price</p>
                        {ISINUnitPrice ? (
                            <p>{formatIndianCurrency(ISINUnitPrice || 0)}</p>
                        ) : (
                            <div className="flex items-center justify-center">
                                <LoadingIcon className="animate-spin" />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-3">
                        <p className="text-sm text-neutral-300">ISIN</p>
                        <p>{stock?.displayISIN ?? stock?.ISIN}</p>
                    </div>
                </div>
                {isExpanded ? (
                    <ChevronUp
                        size={24}
                        className="cursor-pointer transition-transform duration-300"
                        onClick={() => setisExpanded(!isExpanded)}
                    />
                ) : (
                    <ChevronDown
                        size={24}
                        className="cursor-pointer transition-transform duration-300"
                        onClick={() => setisExpanded(!isExpanded)}
                    />
                )}
            </div>
            {isExpanded && (
                <div className="mt-s7 w-fit">
                    <FinancialMetrics
                        isBasket={true}
                        ISINUnitPrice={ISINUnitPrice}
                        stockDetails={stock}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />
                </div>
            )}
        </div>
    ) : (
        <div>
            <div className="w-full border-t-[1px] border-dashed py-s9">
                <div className="flex items-center justify-between gap-4">
                    <Image
                        src={stock?.company?.logoUrl}
                        alt={stock?.company?.displayName}
                        width={69}
                        height={69}
                    />
                    <h3
                        className="line-clamp-2 max-w-[137px] text-lg font-semibold leading-[130%]"
                        title={stock?.company?.displayName}
                    >
                        {stock?.company?.displayName}
                    </h3>
                    {isExpanded ? (
                        <ChevronUp
                            size={24}
                            className="cursor-pointer transition-transform duration-300"
                            onClick={() => setisExpanded(!isExpanded)}
                        />
                    ) : (
                        <ChevronDown
                            size={24}
                            className="cursor-pointer transition-transform duration-300"
                            onClick={() => setisExpanded(!isExpanded)}
                        />
                    )}
                </div>
                <div className="flex items-center justify-between text-center pt-s9 gap-s6">
                    <div className="flex flex-col gap-3">
                        <p className="text-sm text-neutral-300">MARKET CAP</p>
                        <p>{stock?.distribution?.marketCap ?? "--"}</p>
                    </div>

                    <div className="flex flex-col gap-3 text-left">
                        <p className="text-sm text-neutral-300">Current Price</p>
                        {ISINUnitPrice ? (
                            <p>{formatIndianCurrency(ISINUnitPrice || 0)}</p>
                        ) : (
                            <div className="flex items-center justify-center">
                                <LoadingIcon className="animate-spin" />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-3">
                        <p className="text-sm text-neutral-300">ISIN</p>
                        <p>{stock?.displayISIN ?? stock?.ISIN}</p>
                    </div>
                </div>
            </div>
            {isExpanded && (
                <FinancialMetrics
                    isBasket={true}
                    ISINUnitPrice={ISINUnitPrice}
                    stockDetails={stock}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
            )}
        </div>
    );
};
